import type WebSocket from 'ws';
import type { AcpManager } from '../agents/acp/create-acp-manager.js';
import type { CliE2eeRuntime } from '../lib/e2ee/index.js';
import type { PreviewEnvironmentManager } from '../preview-environments/preview-environment-manager.js';
import type { LogFn } from '../log.js';
import type { MessageHandlerDeps } from '../routing/message-handler-deps.js';
import type { PreviewWorktreeManager } from '../worktrees/manager/preview-worktree-manager.js';
import type { SessionWorktreeManager } from '../worktrees/manager.js';
import { createBridgeIdentifiedWithHeartbeat } from './create-bridge-identified-with-heartbeat.js';
import { createBridgeIdentifyHeartbeat } from './create-bridge-identify-heartbeat.js';
import { createBridgeRuntimeMessageDeps } from './create-bridge-runtime-message-deps.js';
import type { BridgeHeartbeatController } from './heartbeat/controller.js';
import type { MainBridgeConnectionState } from './create-main-bridge-reconnect-state.js';
import type { BridgeConnectionTokens, BridgeIdentifyReportedPaths } from './bridge-connection-runtime-types.js';

export function createBridgeRuntimeMessageSetup(options: {
  apiUrl: string;
  workspaceId: string;
  firehoseServerUrl: string | undefined;
  state: MainBridgeConnectionState;
  getWs: () => WebSocket | null;
  log: LogFn;
  tokens: BridgeConnectionTokens;
  acpManager: AcpManager;
  sessionWorktreeManager: SessionWorktreeManager;
  previewWorktreeManager: PreviewWorktreeManager;
  previewEnvironmentManager: PreviewEnvironmentManager;
  e2ee: CliE2eeRuntime | undefined;
  identifyReportedPaths: BridgeIdentifyReportedPaths;
}): {
  bridgeHeartbeat: BridgeHeartbeatController;
  messageDeps: MessageHandlerDeps;
} {
  const {
    apiUrl,
    workspaceId,
    firehoseServerUrl,
    state,
    getWs,
    log,
    tokens,
    acpManager,
    sessionWorktreeManager,
    previewWorktreeManager,
    previewEnvironmentManager,
    e2ee,
    identifyReportedPaths,
  } = options;

  const bridgeHeartbeat = createBridgeIdentifyHeartbeat({
    getWs,
    log,
    identifyReportedPaths,
    e2ee,
  });

  const onBridgeIdentified = createBridgeIdentifiedWithHeartbeat({
    previewEnvironmentManager,
    firehoseServerUrl,
    workspaceId,
    state,
    log,
    bridgeHeartbeat,
  });

  const messageDeps = createBridgeRuntimeMessageDeps({
    apiUrl,
    workspaceId,
    state,
    getWs,
    log,
    tokens,
    acpManager,
    sessionWorktreeManager,
    previewWorktreeManager,
    previewEnvironmentManager,
    e2ee,
    onBridgeIdentified,
    bridgeHeartbeat,
  });

  return { bridgeHeartbeat, messageDeps };
}
